// components/common/ProductCardHorizontal.tsx

import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Heart,
  ShoppingCart,
  Plus,
  Star,
  Sparkles,
} from "lucide-react";

import { useCart } from "@/hooks/useCart";
import { useWishlist } from "@/hooks/useWishlist";
import Placeholder from "@/assets/placeholder.webp";

import {
  Product,
  calculateDiscountPercentage,
  formatPrice,
  isBook,
  formatAuthorsDisplay,
} from "@bookstore/shared";

interface Props {
  product: Product;
}

const TERRACOTTA = "#A8523A";
const TERRACOTTA_DARK = "#8F4430";

const ProductCardHorizontal = ({ product }: Props) => {
  const { isInWishlist, toggleWishlist, isPending: wishlistPending } = useWishlist();
  const { addToCart, isInCart, getItemQuantity, isPending: cartPending } = useCart();

  const [imageLoaded, setImageLoaded] = useState(false);

  const inWishlist = isInWishlist(product.id);
  const inCart = isInCart(product.id);
  const cartQty = getItemQuantity(product.id);

  const discount = product.discountedPrice
    ? calculateDiscountPercentage(product.price, product.discountedPrice)
    : 0;

  const stockStatus =
    product.stock === 0 ? "out" : product.stock <= 5 ? "low" : "in";

  const productUrl = `/product/${product.seoUrl}`;

  const handleAdd = (e: any) => {
    e.preventDefault();
    if (product.stock > 0) addToCart(product.id, 1);
  };

  const handleWishlist = (e: any) => {
    e.preventDefault();
    toggleWishlist(product.id);
  };

  return (
    <Link
      to={productUrl}
      className="
        group flex gap-5 rounded-2xl bg-white 
        border border-[rgba(45,45,45,0.08)] hover:border-primary-200
        hover:shadow-xl transition-all duration-300 p-4
      "
    >
      {/* IMAGE */}
      <div className="relative w-[250px] min-w-[150px] h-[200px] rounded-xl overflow-hidden bg-[rgba(168,82,58,0.05)]">
        {/* Shimmer */}
        {!imageLoaded && (
          <div className="absolute inset-0 bg-gradient-to-r from-primary-200 via-primary-100 to-primary-200 bg-[length:200%_100%] animate-shimmer" />
        )}



         <>
            {/* Blurred background image */}
            <img
              src={product.images?.[0] || Placeholder}
              alt=""
              aria-hidden="true"
              className="absolute inset-0 w-full h-full object-cover blur-sm scale-110 opacity-40"
              loading="lazy"
            />
            {/* Main image - object-contain */}
            <img
               src={product.images?.[0] || Placeholder}
              alt={product.title.slice(0, 50) + (product.title.length > 50 ? '...' : '')}
              onLoad={() => setImageLoaded(true)}
              className={`relative z-0 w-full h-full object-contain p-0 transition-all duration-500 ${
                imageLoaded 
                  ? 'opacity-100 group-hover:scale-105' 
                  : 'opacity-0'
              }`}
              loading="lazy"
            />
          </>

        {/* Discount */}
        {discount > 0 && (
          <div className="absolute top-2 left-2 text-xs px-2 py-1 rounded-md shadow-md text-white bg-gradient-to-r from-red-500 to-orange-500 flex items-center gap-1 font-bold">
            <Sparkles className="w-3 h-3" />
            {discount}% OFF
          </div>
        )}

        {/* Wishlist */}
        <button
          onClick={handleWishlist}
          disabled={wishlistPending}
          className={`
            absolute top-2 right-2 p-2 rounded-md shadow-md backdrop-blur-md transition
            ${inWishlist ? "bg-white text-[#A8523A]" : "bg-white/90 text-gray-600"}
          `}
        >
          <Heart
            className={`w-4 h-4 transition-all ${
              inWishlist ? "fill-[#A8523A] text-[#A8523A]" : ""
            }`}
          />
        </button>

        {/* Low stock */}
        {stockStatus === "low" && (
          <div className="absolute bottom-2 left-2 px-2 py-1 rounded-md text-xs bg-gradient-to-r from-amber-600 to-orange-600 text-white shadow">
            Only {product.stock} left!
          </div>
        )}

        {/* Out of stock */}
        {stockStatus === "out" && (
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center">
            <div className="px-4 py-1 bg-white/20 text-white rounded-xl font-semibold text-sm">
              Out of Stock
            </div>
          </div>
        )}

        {/* Cart Quantity */}
        {inCart && cartQty > 0 && (
          <div className="absolute bottom-2 right-2 px-3 py-1 bg-[#A8523A] text-white text-sm font-bold rounded-md shadow-md">
            {cartQty}
          </div>
        )}
      </div>

      {/* RIGHT SIDE */}
      <div className="flex flex-col justify-between  py-1 flex-1 ">
        {/* Title */}
        <div>
          <h3 className="text-lg font-semibold text-primary leading-snug mb-1">
            {product.title}
          </h3>

          <p className="text-sm text-primary-800 mb-2 truncate" title={isBook(product) ? product.author : product.brand || "Stationery"}>
            {isBook(product) ? formatAuthorsDisplay(product.author, 2) : product.brand || "Stationery"}
          </p>

          {/* Rating */}
          <div className="flex items-center gap-1.5 mb-2">
            <div className="flex items-center gap-0.5">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  className={`w-4 h-4 ${
                    i < Math.floor(product.rating)
                      ? "fill-yellow-400 text-yellow-400"
                      : "fill-gray-200 text-gray-200"
                  }`}
                />
              ))}
            </div>
            <span className="text-sm font-medium text-gray-700">
              {product.rating.toFixed(1)}
            </span>
            <span className="text-xs text-gray-400">({product.reviewCount})</span>
          </div>

          {/* Price */}
          <div className="flex items-baseline gap-2">
            <span className="text-xl font-bold text-primary-900 tracking-tight">
              {formatPrice(product.discountedPrice || product.price)}
            </span>

            {product.discountedPrice && (
              <span className="text-sm text-gray-400 line-through">
                {formatPrice(product.price)}
              </span>
            )}
          </div>
        </div>

        {/* CART BTN */}
        <button
          onClick={handleAdd}
          disabled={product.stock === 0 || cartPending}
          className="
               mt-4 py-2.5 rounded-md font-semibold flex items-center  justify-center gap-2 
            transition-all active:scale-95 w-full
          "
          style={{
            background:
              product.stock === 0
                ? "#e5e5e5"
                : inCart
                ? "linear-gradient(to right, #3A7D44, #4CAF50)"
                : `linear-gradient(to right, ${TERRACOTTA}, ${TERRACOTTA_DARK})`,
            color: product.stock === 0 ? "#999" : "white",
          }}
        >
          {product.stock === 0 ? (
            <>
              <ShoppingCart className="w-4 h-4" />
              Out of Stock
            </>
          ) : inCart ? (
            <>
              <Plus className="w-4 h-4" />
              Add More
            </>
          ) : (
            <>
              <ShoppingCart className="w-4 h-4" />
              Add to Cart
            </>
          )}
        </button>
      </div>
    </Link>
  );
};

export default ProductCardHorizontal;
